import { ApiError, get, post, del, type Me, type Onboarding } from '../api.js';
import { button, chip, clear, el, field, input, panel, row, select, skeleton, submitButton, toast } from '../ui.js';

const US_STATES: Array<[string, string]> = [
  ['', 'Choose a state…'], ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'], ['DC', 'District of Columbia'],
  ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'],
  ['IA', 'Iowa'], ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'],
  ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'], ['MS', 'Mississippi'], ['MO', 'Missouri'],
  ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'], ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'], ['SC', 'South Carolina'],
  ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'],
  ['VA', 'Virginia'], ['WA', 'Washington'], ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

/**
 * The second track is not always the agency: a manager gets their team, a
 * compliance officer gets the compliance desk, an agent gets nothing at all.
 * The API says which, and names it. `api.ts` still types the older shape, so
 * the two names are read through this narrowing until it catches up.
 */
type Track = Onboarding['agency'] & { label?: string; blurb?: string };

/** Which of the two lists a card belongs to. The same key means different
 *  things in each: an agent joins a team, a manager creates one. */
type Scope = 'you' | 'track';

export function onboardingView(me: Me, refresh: () => void, go: (p: string) => void): HTMLElement {
  const host = el('div');
  void render();

  async function render(): Promise<void> {
    clear(host);
    host.append(el('div', { class: 'vhead' },
      el('span', { class: 'kicker', text: 'Getting started' }),
      el('h1', { text: `Welcome, ${me.user.firstName}` }),
      el('p', { text: 'A few things before the phones start. Each step below is ticked by the system when the thing it asks for actually exists — there is no box to fool.' })));
    host.append(skeleton(4));

    let ob: Onboarding;
    try {
      ob = await get<Onboarding>('/v1/onboarding');
    } catch (e) {
      clear(host);
      host.append(el('div', { class: 'warnbox' }, e instanceof ApiError ? e.message : 'Could not load your setup.'));
      return;
    }

    clear(host);
    host.append(el('div', { class: 'vhead' },
      el('span', { class: 'kicker', text: 'Getting started' }),
      el('h1', { text: `Welcome, ${me.user.firstName}` }),
      el('p', { text: 'Each step is ticked when the thing it asks for exists. Nothing here is a checkbox you can lie to.' })));

    const track = ob.agency as Track;
    const total = ob.you.steps.length + track.steps.length;
    const done = [...ob.you.steps, ...track.steps].filter((s) => s.done).length;
    host.append(panel(null,
      el('div', { style: 'display:flex;justify-content:space-between;font-size:12.5px;margin-bottom:7px' },
        el('span', { text: 'Setup progress' }),
        el('span', { class: 'mono', text: `${done} of ${total}` })),
      el('div', { class: 'bar' }, el('i', { style: `width:${Math.round((done / Math.max(1, total)) * 100)}%` }))));

    host.append(stepsPanel('Your account', null, ob.you.steps, 'you'));
    if (track.steps.length) {
      host.append(stepsPanel(track.label ?? 'Your agency', track.blurb ?? null, track.steps, 'track'));
    }

    if (ob.you.complete) {
      // Finishing turns on the desk once your own account is in order. Say so
      // plainly when the second track still has something waiting, rather than
      // claiming everything is done.
      const waiting = track.steps.find((s) => !s.done && !s.optional);
      host.append(panel(null,
        el('div', { class: 'okbox' }, waiting
          ? `Your account is set up. "${waiting.title}" is still open on ${(track.label ?? 'your agency').toLowerCase()} — you can go in now and come back to it.`
          : 'Everything on your side is done.'),
        el('div', { style: 'margin-top:12px' },
          button('Finish setup and open the desk', 'pri', async () => {
            try {
              await post('/v1/onboarding/finish');
              toast('Setup complete.', 'ok');
              refresh();
            } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not finish setup.', 'crit'); }
          }))));
    }
  }

  function stepsPanel(title: string, blurb: string | null, steps: Onboarding['you']['steps'], scope: Scope): HTMLElement {
    const list = el('div', { class: 'steps' });
    for (const s of steps) list.append(stepCard(s, scope));
    return panel(title,
      blurb ? el('p', { class: 's', style: 'color:var(--ink-3);font-size:12.5px;margin:-6px 0 14px', text: blurb }) : null,
      list);
  }

  function stepCard(s: Onboarding['you']['steps'][number], scope: Scope): HTMLElement {
    const body = el('div', { style: 'flex:1;min-width:0' },
      el('h3', { text: s.title }),
      el('p', { text: s.blurb }));
    const card = el('div', { class: `step${s.done ? ' done' : ''}${s.current ? ' current' : ''}` },
      el('div', { class: 'mark', 'aria-hidden': 'true', text: s.done ? '✓' : '' }),
      body);
    if (!s.done) {
      const action = actionFor(s.key, scope);
      if (action) body.append(el('div', { style: 'margin-top:10px' }, action));
    }
    // Say so on every optional step, not only the finished ones: half of what a
    // manager or compliance officer is shown can be left for later.
    if (s.optional) body.append(el('div', { style: 'margin-top:8px' }, chip('optional')));
    return card;
  }

  function actionFor(key: string, scope: Scope): HTMLElement | null {
    switch (key) {
      case 'profile': return button('Fill in your profile', 'sm pri', () => { profileForm(); });
      case 'licences': return button('Add a licence', 'sm pri', () => { licenceForm(); });
      case 'mfa': return button('Turn on multi-factor', 'sm pri', () => go('/settings/security'));
      // On your own list this is joining a desk; on a leader's list it is
      // having one at all.
      case 'team': return scope === 'track'
        ? button('Create a team', 'sm pri', () => go('/teams'))
        : button('Find a team', 'sm pri', () => go('/teams'));
      case 'states': return button('Set the states you cover', 'sm pri', () => go('/teams'));
      case 'agents': return button('Bring your agents in', 'sm', () => go('/teams'));
      case 'agency': return button('Agency details', 'sm', () => go('/settings/agency'));
      case 'compliance': return button('Set the calling rules', 'sm', () => go('/settings/compliance'));
      case 'dnc': return button('Record a do-not-call', 'sm', () => go('/compliance'));
      case 'audit': return button('Open the audit trail', 'sm', () => go('/settings/audit'));
      case 'source': return button('Add a lead source', 'sm', () => go('/settings/sources'));
      case 'import': return button('Import a list', 'sm pri', () => go('/import'));
      case 'invite': return button('Invite your agents', 'sm', () => go('/people'));
      default: return null;
    }
  }

  function profileForm(): void {
    const phone = input({ type: 'tel', value: me.user.phone ?? '', autocomplete: 'tel', placeholder: '(305) 249-0142' });
    const city = input({ value: me.user.city ?? '', autocomplete: 'address-level2' });
    const state = select(US_STATES, me.user.state ?? '');
    const save = submitButton('Save', 'pri');
    const form = el('form', {
      onsubmit: async (e: Event) => {
        e.preventDefault();
        save.disabled = true;
        try {
          await post('/v1/onboarding/profile', { phone: phone.value, city: city.value, state: state.value });
          toast('Profile saved.', 'ok');
          refresh();
        } catch (e2) { toast(e2 instanceof ApiError ? e2.message : 'Could not save.', 'crit'); } finally { save.disabled = false; }
      },
    },
      field('Mobile number', phone, 'Where the agency reaches you, not a lead-facing number.'),
      field('City', city),
      field('State you work from', state, 'This is what "teams near me" uses.'),
      save);
    replaceInline('Your profile', form);
  }

  function licenceForm(): void {
    const state = select(US_STATES);
    const num = input({ placeholder: 'As printed on the licence' });
    const exp = input({ type: 'date' });
    const save = submitButton('Add licence', 'pri');
    const list = el('div', { class: 'rows' });

    const load = async (): Promise<void> => {
      const fresh = await get<Me>('/v1/me');
      clear(list);
      if (fresh.licences.length === 0) {
        list.append(el('p', { class: 's', style: 'color:var(--ink-3);font-size:12.5px', text: 'No licences on file yet.' }));
      }
      for (const l of fresh.licences) {
        list.append(row({
          title: `${l.state} — ${l.type}`,
          sub: l.expiresAt ? `Expires ${new Date(l.expiresAt).toLocaleDateString()}` : 'No expiry recorded',
          right: button('Remove', 'sm ghost', async () => {
            await del(`/v1/onboarding/licences/${l.id}`);
            toast('Licence removed.', 'ok');
            await load();
            refresh();
          }),
        }));
      }
    };

    const form = el('form', {
      onsubmit: async (e: Event) => {
        e.preventDefault();
        if (!state.value) { toast('Choose a state first.', 'crit'); return; }
        save.disabled = true;
        try {
          await post('/v1/onboarding/licences', {
            state: state.value,
            licenseNumber: num.value || undefined,
            expiresAt: exp.value || undefined,
          });
          toast('Licence added.', 'ok');
          num.value = '';
          await load();
          refresh();
        } catch (e2) { toast(e2 instanceof ApiError ? e2.message : 'Could not add that licence.', 'crit'); } finally { save.disabled = false; }
      },
    },
      el('div', { class: 'note', style: 'margin-bottom:12px' },
        'Routing will not send you a lead in a state you are not licensed for. This list decides what work reaches you.'),
      field('State', state),
      field('Licence number', num),
      field('Expires', exp, 'We warn you 60 days out, and stop routing when it lapses.'),
      save,
      el('div', { style: 'margin-top:16px' }, list));
    void load();
    replaceInline('Your licences', form);
  }

  function replaceInline(title: string, form: HTMLElement): void {
    clear(host);
    host.append(el('div', { class: 'vhead' },
      el('span', { class: 'kicker', text: 'Getting started' }),
      el('h1', { text: title })));
    host.append(panel(null, form));
    host.append(el('div', { style: 'margin-top:8px' }, button('Back to setup', 'ghost', () => { void render(); })));
  }

  return host;
}
